import { encodeToMp3, sniffSourceFormat } from '../../audio/audioWorkerClient'
import { computeTargetKbpsForSize } from '../../audio/computeTargetKbps'
import { TARGET_ENCODED_SIZE_BYTES } from '../../audio/types'
import { fetchEpisodeAudio } from './fetchEpisodeAudio'
import type { Episode } from './types'

export const DOWNLOAD_CONCURRENCY = 3

export type EpisodePipelineEvent =
  | { type: 'downloading'; index: number }
  | { type: 'queued'; index: number }
  | { type: 'encoding'; index: number; percent: number }
  | { type: 'done'; index: number }
  | { type: 'failed'; index: number; stage: 'download' | 'encode' }

export type EpisodePipelineResult = {
  // Parallel to the input array; undefined marks a failed episode.
  blobs: (Blob | undefined)[]
  failedTitles: string[]
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

// Downloads and encodes episodes with a bounded worker pool. Each pool
// worker downloads its episode and then encodes it before pulling the
// next index, so up to DOWNLOAD_CONCURRENCY encodes run in parallel
// (the audio worker pool gives each one its own wasm LAME instance) and
// downloads overlap encodes across workers. A worker holds one episode
// at a time, which bounds raw downloaded blobs in memory to the pool
// width.
export async function runEpisodePipeline(
  episodes: Pick<Episode, 'title' | 'audio'>[],
  onEvent: (e: EpisodePipelineEvent) => void,
): Promise<EpisodePipelineResult> {
  const blobs: (Blob | undefined)[] = new Array(episodes.length)
  const failedIdx: number[] = []
  let cursor = 0

  const encodeOne = async (index: number, downloaded: Blob) => {
    // Provisional selection-index name; final contiguous names are
    // assigned by the caller after failures are known.
    const file = new File([downloaded], `${pad2(index + 1)}.mp3`, {
      type: 'audio/mpeg',
    })
    onEvent({ type: 'encoding', index, percent: 0 })
    const sniffed = await sniffSourceFormat(file)
    const targetKbps = sniffed
      ? computeTargetKbpsForSize(file.size, sniffed.kbps, TARGET_ENCODED_SIZE_BYTES)
      : undefined
    const { blob } = await encodeToMp3(file, {
      targetKbps,
      onProgress: (percent) => onEvent({ type: 'encoding', index, percent }),
    })
    blobs[index] = blob
  }

  const worker = async () => {
    while (cursor < episodes.length) {
      const index = cursor++
      onEvent({ type: 'downloading', index })
      let downloaded: Blob
      try {
        downloaded = await fetchEpisodeAudio(episodes[index].audio)
      } catch {
        failedIdx.push(index)
        onEvent({ type: 'failed', index, stage: 'download' })
        continue
      }
      onEvent({ type: 'queued', index })
      try {
        await encodeOne(index, downloaded)
        onEvent({ type: 'done', index })
      } catch {
        failedIdx.push(index)
        onEvent({ type: 'failed', index, stage: 'encode' })
      }
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(DOWNLOAD_CONCURRENCY, episodes.length) }, () =>
      worker(),
    ),
  )

  failedIdx.sort((a, b) => a - b)
  return { blobs, failedTitles: failedIdx.map((i) => episodes[i].title) }
}
